import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import CampaignComposer from './CampaignComposer';
import CampaignAnalytics from './CampaignAnalytics';
import {
  LayoutDashboard, Users, BarChart3, Mail, LogOut,
  Search, RefreshCw, MapPin, Phone, Calendar, 
  TrendingUp, UserCheck, Building2, Home, Briefcase, Send,
  CheckCircle, Clock, XCircle, Eye, ChevronLeft, ChevronRight,
  Download, StickyNote, Menu, X, Filter, Megaphone, PenSquare
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status badge component
const StatusBadge = ({ status }) => {
  const styles = {
    NEW: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    CONTACTED: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    QUALIFIED: 'bg-green-500/20 text-green-400 border-green-500/30',
    CLOSED: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };
  
  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[status] || styles.NEW}`}>
      {status}
    </span>
  );
};

// Buyer type icon
const BuyerTypeIcon = ({ type }) => {
  const icons = {
    buyer: Home,
    broker: Building2,
    investor: TrendingUp,
    relocation: Briefcase
  };
  const Icon = icons[type] || Users;
  return <Icon className="w-4 h-4" />;
};

// Stat card component - mobile optimized
const StatCard = ({ icon: Icon, label, value, color = 'gold' }) => (
  <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-3 sm:p-5">
    <div className="flex items-center sm:items-start justify-between">
      <div className="flex items-center sm:block gap-3">
        <div className={`p-2 sm:hidden rounded-lg ${color === 'gold' ? 'bg-[#d4af37]/10' : 'bg-gray-800'}`}>
          <Icon className={`w-4 h-4 ${color === 'gold' ? 'text-[#d4af37]' : 'text-gray-400'}`} />
        </div>
        <div>
          <p className="text-gray-400 text-xs sm:text-sm mb-0 sm:mb-1">{label}</p>
          <p className={`text-xl sm:text-3xl font-bold ${color === 'gold' ? 'text-[#d4af37]' : 'text-white'}`}>
            {value}
          </p>
        </div>
      </div>
      <div className={`hidden sm:flex p-3 rounded-lg ${color === 'gold' ? 'bg-[#d4af37]/10' : 'bg-gray-800'}`}>
        <Icon className={`w-5 h-5 ${color === 'gold' ? 'text-[#d4af37]' : 'text-gray-400'}`} />
      </div>
    </div>
  </div>
);

// Campaigns Tab with toggle between Compose and Analytics
const CampaignsTab = ({ leads, onCampaignSent }) => {
  const [view, setView] = useState('compose'); // 'compose' or 'analytics'

  return (
    <div className="space-y-6">
      {/* View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <button
            onClick={() => setView('compose')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              view === 'compose'
                ? 'bg-[#d4af37] text-[#0a0d14]'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            data-testid="campaigns-compose-btn"
          >
            <PenSquare className="w-4 h-4" />
            Compose
          </button>
          <button
            onClick={() => setView('analytics')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              view === 'analytics'
                ? 'bg-[#d4af37] text-[#0a0d14]'
                : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
            }`}
            data-testid="campaigns-analytics-btn"
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
          </button>
        </div>
      </div>

      {/* Content */}
      {view === 'compose' ? (
        <div>
          <div className="mb-6">
            <h2 className="text-xl font-serif text-white mb-2">Compose Campaign</h2>
            <p className="text-gray-400 text-sm">Send property updates and information to your leads or imported contacts.</p>
          </div>
          <CampaignComposer 
            leads={leads} 
            onCampaignSent={() => {
              onCampaignSent();
              setView('analytics'); // Switch to analytics after sending
            }}
          />
        </div>
      ) : (
        <CampaignAnalytics />
      )}
    </div>
  );
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [activeTab, setActiveTab] = useState('leads');
  const [leads, setLeads] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({ status: '', buyer_type: '', start_date: '', end_date: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [sendingReport, setSendingReport] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesText, setNotesText] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [analyticsDateRange, setAnalyticsDateRange] = useState({ start: '', end: '' });

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filters.status) params.append('status', filters.status);
      if (filters.buyer_type) params.append('buyer_type', filters.buyer_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await axios.get(`${API_URL}/api/admin/leads?${params}`, {
        withCredentials: true
      });
      setLeads(response.data.leads);
      setTotalPages(response.data.pages);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  }, [page, filters]);

  // Fetch analytics
  const fetchAnalytics = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (analyticsDateRange.start) params.append('start_date', analyticsDateRange.start);
      if (analyticsDateRange.end) params.append('end_date', analyticsDateRange.end);
      
      const response = await axios.get(`${API_URL}/api/admin/analytics?${params}`, {
        withCredentials: true
      });
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  }, [analyticsDateRange]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLeads(), fetchAnalytics()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLeads, fetchAnalytics]);

  // Update lead status
  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(
        `${API_URL}/api/admin/leads/${leadId}`,
        { status: newStatus },
        { withCredentials: true }
      );
      await fetchLeads();
      await fetchAnalytics();
      if (selectedLead?.id === leadId) {
        setSelectedLead(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      console.error('Error updating lead:', error);
    }
  };

  // Save lead notes
  const saveNotes = async () => {
    if (!selectedLead) return;
    setSavingNotes(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/leads/${selectedLead.id}`,
        { notes: notesText },
        { withCredentials: true }
      );
      setSelectedLead(prev => ({ ...prev, notes: notesText }));
      setEditingNotes(false);
      await fetchLeads();
    } catch (error) {
      console.error('Error saving notes:', error);
    } finally {
      setSavingNotes(false);
    }
  };

  // Export CSV
  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.buyer_type) params.append('buyer_type', filters.buyer_type);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      
      const response = await axios.get(`${API_URL}/api/admin/leads/export/csv?${params}`, {
        withCredentials: true,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `william-lane-estate-leads-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Failed to export leads');
    } finally {
      setExporting(false);
    }
  };

  // Send report
  const sendReport = async (type) => {
    setSendingReport(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/reports/send?report_type=${type}`,
        {},
        { withCredentials: true }
      );
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} report sent successfully!`);
    } catch (error) {
      alert('Failed to send report. Please try again.');
    } finally {
      setSendingReport(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  // Filter leads by search
  const filteredLeads = leads.filter(lead => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      lead.name?.toLowerCase().includes(term) ||
      lead.email?.toLowerCase().includes(term) ||
      lead.phone?.includes(term)
    );
  });

  // Get buyer type label
  const getBuyerTypeLabel = (type) => {
    const labels = {
      buyer: 'Private Buyer',
      broker: 'Buyer Broker',
      investor: 'Investor / Advisor',
      relocation: 'Business Owner / Relocation'
    };
    return labels[type] || type;
  };

  // Format date
  const formatDate = (dateStr, short = false) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (short) {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0d14] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#d4af37]/30 border-t-[#d4af37] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0d14]">
      {/* Header - Mobile Optimized */}
      <header className="bg-[#111827]/80 backdrop-blur-xl border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Logo */}
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-[#d4af37]/10 rounded-lg flex items-center justify-center">
                <LayoutDashboard className="w-4 h-4 sm:w-5 sm:h-5 text-[#d4af37]" />
              </div>
              <div>
                <h1 className="text-white font-serif text-sm sm:text-lg">Estate Admin</h1>
                <p className="text-[10px] sm:text-xs text-gray-500 hidden sm:block">William Lane Estate</p>
              </div>
            </div>

            {/* Desktop User Menu */}
            <div className="hidden sm:flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-white">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                data-testid="admin-logout-button"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="sm:hidden p-2 text-gray-400 hover:text-white"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="sm:hidden bg-[#111827] border-t border-gray-800 px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-sm text-white">{user?.name}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-400 text-sm"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Tab Navigation - Mobile Optimized */}
      <div className="bg-[#111827]/40 border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <nav className="flex gap-0 sm:gap-1 overflow-x-auto">
            {[
              { id: 'leads', label: 'Leads', icon: Users },
              { id: 'campaigns', label: 'Campaigns', icon: Megaphone },
              { id: 'analytics', label: 'Analytics', icon: BarChart3 },
              { id: 'reports', label: 'Reports', icon: Mail }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => { setActiveTab(tab.id); setMobileMenuOpen(false); }}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-[#d4af37] border-[#d4af37]'
                    : 'text-gray-400 border-transparent hover:text-white hover:border-gray-600'
                }`}
                data-testid={`admin-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Stats Overview - Mobile Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 mb-4 sm:mb-8">
          <StatCard icon={Users} label="Total Leads" value={analytics?.total_leads || 0} />
          <StatCard icon={UserCheck} label="Qualified" value={analytics?.statuses?.QUALIFIED || 0} color="white" />
          <StatCard icon={Clock} label="New" value={analytics?.statuses?.NEW || 0} color="white" />
          <StatCard icon={CheckCircle} label="Contacted" value={analytics?.statuses?.CONTACTED || 0} color="white" />
        </div>

        {/* LEADS TAB */}
        {activeTab === 'leads' && (
          <div className="space-y-4 sm:space-y-6">
            {/* Filters - Mobile Collapsible */}
            <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-3 sm:p-4">
              {/* Mobile Filter Toggle */}
              <div className="flex items-center justify-between mb-3 sm:hidden">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center gap-2 text-gray-400 text-sm"
                >
                  <Filter className="w-4 h-4" />
                  {showFilters ? 'Hide Filters' : 'Show Filters'}
                </button>
                <div className="flex gap-2">
                  <button
                    onClick={exportCSV}
                    disabled={exporting}
                    className="p-2 text-[#d4af37] bg-[#d4af37]/10 rounded-lg"
                    data-testid="export-csv-btn-mobile"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { fetchLeads(); fetchAnalytics(); }}
                    className="p-2 text-gray-400 hover:text-[#d4af37] rounded-lg"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Filter Controls */}
              <div className={`flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 sm:gap-4 ${showFilters ? 'block' : 'hidden sm:flex'}`}>
                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input
                    type="text"
                    placeholder="Search leads..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-[#0a0d14] border border-gray-700 rounded-lg pl-10 pr-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#d4af37]"
                    data-testid="admin-search-input"
                  />
                </div>

                {/* Date Range */}
                <div className="flex gap-2 flex-1 sm:flex-none">
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                    className="flex-1 sm:w-36 bg-[#0a0d14] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d4af37]"
                    data-testid="admin-start-date"
                  />
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                    className="flex-1 sm:w-36 bg-[#0a0d14] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d4af37]"
                    data-testid="admin-end-date"
                  />
                </div>

                {/* Status Filter */}
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="bg-[#0a0d14] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d4af37]"
                  data-testid="admin-status-filter"
                >
                  <option value="">All Statuses</option>
                  <option value="NEW">New</option>
                  <option value="CONTACTED">Contacted</option>
                  <option value="QUALIFIED">Qualified</option>
                  <option value="CLOSED">Closed</option>
                </select>

                {/* Buyer Type Filter */}
                <select
                  value={filters.buyer_type}
                  onChange={(e) => setFilters(prev => ({ ...prev, buyer_type: e.target.value }))}
                  className="bg-[#0a0d14] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d4af37]"
                  data-testid="admin-buyer-type-filter"
                >
                  <option value="">All Types</option>
                  <option value="buyer">Private Buyer</option>
                  <option value="broker">Buyer Broker</option>
                  <option value="investor">Investor</option>
                  <option value="relocation">Relocation</option>
                </select>

                {/* Desktop Actions */}
                <div className="hidden sm:flex items-center gap-2">
                  <button
                    onClick={exportCSV}
                    disabled={exporting}
                    className="flex items-center gap-2 px-4 py-2 bg-[#d4af37]/10 text-[#d4af37] rounded-lg text-sm font-medium hover:bg-[#d4af37]/20 transition-colors disabled:opacity-50"
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4" />
                    {exporting ? 'Exporting...' : 'Export CSV'}
                  </button>
                  <button
                    onClick={() => { fetchLeads(); fetchAnalytics(); }}
                    className="p-2 text-gray-400 hover:text-[#d4af37] hover:bg-[#d4af37]/10 rounded-lg transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Leads Table - Mobile Card View */}
            <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl overflow-hidden">
              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#0a0d14]/50">
                    <tr>
                      <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Contact</th>
                      <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Type</th>
                      <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Location</th>
                      <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Source</th>
                      <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Status</th>
                      <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Date</th>
                      <th className="text-left text-xs font-medium text-gray-400 uppercase tracking-wider px-4 py-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {filteredLeads.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                          No leads found
                        </td>
                      </tr>
                    ) : (
                      filteredLeads.map(lead => (
                        <tr
                          key={lead.id}
                          className="hover:bg-[#0a0d14]/30 transition-colors cursor-pointer"
                          onClick={() => { setSelectedLead(lead); setNotesText(lead.notes || ''); setEditingNotes(false); }}
                          data-testid={`lead-row-${lead.id}`}
                        >
                          <td className="px-4 py-4">
                            <div>
                              <p className="text-white font-medium">{lead.name}</p>
                              <p className="text-sm text-gray-400">{lead.email}</p>
                              {lead.phone && (
                                <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                  <Phone className="w-3 h-3" />
                                  {lead.phone}
                                </p>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2 text-gray-300">
                              <BuyerTypeIcon type={lead.buyer_type} />
                              <span className="text-sm">{getBuyerTypeLabel(lead.buyer_type)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {lead.location?.city && lead.location.city !== 'Unknown' ? (
                              <div className="flex items-center gap-1 text-sm text-gray-400">
                                <MapPin className="w-3 h-3" />
                                {lead.location.city}, {lead.location.region}
                              </div>
                            ) : (
                              <span className="text-gray-600 text-sm">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm text-gray-400">
                              {lead.utm_source || lead.referrer || 'Direct'}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <StatusBadge status={lead.status} />
                          </td>
                          <td className="px-4 py-4">
                            <span className="text-sm text-gray-400">{formatDate(lead.created_at)}</span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-1">
                              {lead.notes && <StickyNote className="w-3 h-3 text-[#d4af37]" />}
                              <button
                                onClick={(e) => { e.stopPropagation(); setSelectedLead(lead); setNotesText(lead.notes || ''); setEditingNotes(false); }}
                                className="p-2 text-gray-400 hover:text-[#d4af37] hover:bg-[#d4af37]/10 rounded-lg transition-colors"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile Card View */}
              <div className="sm:hidden divide-y divide-gray-800">
                {filteredLeads.length === 0 ? (
                  <div className="px-4 py-12 text-center text-gray-500">No leads found</div>
                ) : (
                  filteredLeads.map(lead => (
                    <div
                      key={lead.id}
                      className="p-4 hover:bg-[#0a0d14]/30 transition-colors cursor-pointer"
                      onClick={() => { setSelectedLead(lead); setNotesText(lead.notes || ''); setEditingNotes(false); }}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-white font-medium">{lead.name}</p>
                          <p className="text-xs text-gray-400">{lead.email}</p>
                        </div>
                        <StatusBadge status={lead.status} />
                      </div>
                      <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <BuyerTypeIcon type={lead.buyer_type} />
                          {getBuyerTypeLabel(lead.buyer_type).split(' ')[0]}
                        </span>
                        <span>{formatDate(lead.created_at, true)}</span>
                        {lead.notes && <StickyNote className="w-3 h-3 text-[#d4af37]" />}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t border-gray-800">
                  <p className="text-xs sm:text-sm text-gray-400">
                    Page {page} of {totalPages}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && analytics && (
          <div className="space-y-4 sm:space-y-6">
            {/* Date Range Selector */}
            <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4">
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <span className="text-gray-400 text-sm">Date Range:</span>
                <input
                  type="date"
                  value={analyticsDateRange.start}
                  onChange={(e) => setAnalyticsDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="flex-1 sm:flex-none sm:w-40 bg-[#0a0d14] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d4af37]"
                  data-testid="analytics-start-date"
                />
                <span className="text-gray-500 hidden sm:inline">to</span>
                <input
                  type="date"
                  value={analyticsDateRange.end}
                  onChange={(e) => setAnalyticsDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="flex-1 sm:flex-none sm:w-40 bg-[#0a0d14] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#d4af37]"
                  data-testid="analytics-end-date"
                />
                <button
                  onClick={() => setAnalyticsDateRange({ start: '', end: '' })}
                  className="text-xs text-gray-400 hover:text-[#d4af37]"
                >
                  Reset to 30 days
                </button>
              </div>
            </div>

            {/* Buyer Types */}
            <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4 sm:p-6">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2 text-sm sm:text-base">
                <Users className="w-5 h-5 text-[#d4af37]" />
                Inquiries by Buyer Type
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                {Object.entries(analytics.buyer_types || {}).map(([type, count]) => (
                  <div key={type} className="bg-[#0a0d14]/50 rounded-lg p-3 sm:p-4 text-center">
                    <div className="flex justify-center mb-2 text-[#d4af37]">
                      <BuyerTypeIcon type={type} />
                    </div>
                    <p className="text-xl sm:text-2xl font-bold text-[#d4af37]">{count}</p>
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-1">{getBuyerTypeLabel(type)}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Traffic Sources & Locations */}
            <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4 sm:p-6">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2 text-sm sm:text-base">
                  <TrendingUp className="w-5 h-5 text-[#d4af37]" />
                  Traffic Sources
                </h3>
                <div className="space-y-3">
                  {Object.entries(analytics.traffic_sources || {})
                    .sort(([,a], [,b]) => b - a)
                    .slice(0, 10)
                    .map(([source, count]) => (
                      <div key={source} className="flex items-center justify-between">
                        <span className="text-gray-300 text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[200px]">{source}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-16 sm:w-24 h-2 bg-gray-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-[#d4af37] rounded-full"
                              style={{ width: `${(count / analytics.total_leads) * 100}%` }}
                            />
                          </div>
                          <span className="text-[#d4af37] text-xs sm:text-sm font-medium w-6 sm:w-8 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Locations */}
              <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4 sm:p-6">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2 text-sm sm:text-base">
                  <MapPin className="w-5 h-5 text-[#d4af37]" />
                  Visitor Locations
                </h3>
                <div className="space-y-3">
                  {Object.entries(analytics.locations || {}).length === 0 ? (
                    <p className="text-gray-500 text-sm">No location data yet</p>
                  ) : (
                    Object.entries(analytics.locations)
                      .slice(0, 10)
                      .map(([location, count]) => (
                        <div key={location} className="flex items-center justify-between">
                          <span className="text-gray-300 text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[200px]">{location}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-16 sm:w-24 h-2 bg-gray-800 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-[#d4af37] rounded-full"
                                style={{ width: `${(count / analytics.total_leads) * 100}%` }}
                              />
                            </div>
                            <span className="text-[#d4af37] text-xs sm:text-sm font-medium w-6 sm:w-8 text-right">{count}</span>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>

            {/* Daily Trend */}
            <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-4 sm:p-6">
              <h3 className="text-white font-medium mb-4 flex items-center gap-2 text-sm sm:text-base">
                <Calendar className="w-5 h-5 text-[#d4af37]" />
                Daily Inquiries
              </h3>
              <div className="h-32 sm:h-40 flex items-end gap-0.5 sm:gap-1">
                {Object.entries(analytics.daily_trend || {}).slice(-30).map(([date, count]) => {
                  const maxCount = Math.max(...Object.values(analytics.daily_trend || { '': 1 }));
                  const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
                  return (
                    <div key={date} className="flex-1 group relative">
                      <div
                        className="bg-[#d4af37]/60 hover:bg-[#d4af37] rounded-t transition-colors"
                        style={{ height: `${Math.max(height, 4)}%` }}
                      />
                      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                        {date}: {count}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* CAMPAIGNS TAB */}
        {activeTab === 'campaigns' && (
          <CampaignsTab leads={leads} onCampaignSent={() => { fetchLeads(); fetchAnalytics(); }} />
        )}

        {/* REPORTS TAB */}
        {activeTab === 'reports' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-[#111827]/60 backdrop-blur border border-gray-800 rounded-xl p-6 sm:p-8">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-[#d4af37]/10 rounded-full mb-4">
                  <Mail className="w-7 h-7 sm:w-8 sm:h-8 text-[#d4af37]" />
                </div>
                <h2 className="text-lg sm:text-xl font-serif text-white mb-2">Email Reports</h2>
                <p className="text-gray-400 text-xs sm:text-sm">
                  Send analytics reports to TheMarketSource@gmail.com
                </p>
              </div>

              <div className="space-y-4">
                <div className="bg-[#0a0d14]/50 rounded-lg p-4 sm:p-5 border border-gray-800">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div>
                      <h3 className="text-white font-medium mb-1 text-sm sm:text-base">Weekly Report</h3>
                      <p className="text-xs sm:text-sm text-gray-400">
                        Summary of last 7 days. Auto-sent every Sunday at 8 AM UTC.
                      </p>
                    </div>
                    <button
                      onClick={() => sendReport('weekly')}
                      disabled={sendingReport}
                      className="flex items-center justify-center gap-2 bg-[#d4af37] hover:bg-[#c4a030] text-[#0a0d14] px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 w-full sm:w-auto"
                      data-testid="send-weekly-report-btn"
                    >
                      <Send className="w-4 h-4" />
                      Send Now
                    </button>
                  </div>
                </div>

                <div className="bg-[#0a0d14]/50 rounded-lg p-4 sm:p-5 border border-gray-800">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                    <div>
                      <h3 className="text-white font-medium mb-1 text-sm sm:text-base">Monthly Report</h3>
                      <p className="text-xs sm:text-sm text-gray-400">
                        Full month analysis. Auto-sent 1st of each month at 8 AM UTC.
                      </p>
                    </div>
                    <button
                      onClick={() => sendReport('monthly')}
                      disabled={sendingReport}
                      className="flex items-center justify-center gap-2 bg-[#d4af37] hover:bg-[#c4a030] text-[#0a0d14] px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 w-full sm:w-auto"
                      data-testid="send-monthly-report-btn"
                    >
                      <Send className="w-4 h-4" />
                      Send Now
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-6 sm:mt-8 p-3 sm:p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                <p className="text-xs sm:text-sm text-blue-300">
                  <strong>Note:</strong> Reports are automatically sent on schedule. Use "Send Now" to manually trigger a report at any time.
                </p>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Lead Detail Modal - Mobile Optimized */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setSelectedLead(null)}>
          <div className="bg-[#111827] border-t sm:border border-gray-800 rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#111827] border-b border-gray-800 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-serif text-white">Lead Details</h3>
              <button
                onClick={() => setSelectedLead(null)}
                className="text-gray-400 hover:text-white p-1"
              >
                <XCircle className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 sm:p-6 space-y-5 sm:space-y-6">
              {/* Contact Info */}
              <div>
                <h4 className="text-[#d4af37] text-xs font-medium mb-3 uppercase tracking-wider">Contact Information</h4>
                <div className="bg-[#0a0d14]/50 rounded-lg p-3 sm:p-4 space-y-2 sm:space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Name</span>
                    <span className="text-white font-medium">{selectedLead.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Email</span>
                    <a href={`mailto:${selectedLead.email}`} className="text-[#d4af37] hover:underline truncate ml-4">{selectedLead.email}</a>
                  </div>
                  {selectedLead.phone && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Phone</span>
                      <a href={`tel:${selectedLead.phone}`} className="text-white">{selectedLead.phone}</a>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Buyer Type</span>
                    <span className="text-white">{getBuyerTypeLabel(selectedLead.buyer_type)}</span>
                  </div>
                  {selectedLead.location?.city && selectedLead.location.city !== 'Unknown' && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">Location</span>
                      <span className="text-white">{selectedLead.location.city}, {selectedLead.location.region}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Message */}
              {selectedLead.message && (
                <div>
                  <h4 className="text-[#d4af37] text-xs font-medium mb-3 uppercase tracking-wider">Message</h4>
                  <div className="bg-[#0a0d14]/50 rounded-lg p-3 sm:p-4">
                    <p className="text-gray-300 text-sm whitespace-pre-wrap">{selectedLead.message}</p>
                  </div>
                </div>
              )}

              {/* Notes Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-[#d4af37] text-xs font-medium uppercase tracking-wider flex items-center gap-2">
                    <StickyNote className="w-4 h-4" />
                    Private Notes
                  </h4>
                  {!editingNotes && (
                    <button
                      onClick={() => setEditingNotes(true)}
                      className="text-xs text-gray-400 hover:text-[#d4af37]"
                    >
                      {selectedLead.notes ? 'Edit' : 'Add Note'}
                    </button>
                  )}
                </div>
                {editingNotes ? (
                  <div className="space-y-3">
                    <textarea
                      value={notesText}
                      onChange={(e) => setNotesText(e.target.value)}
                      placeholder="Add private notes about this lead..."
                      className="w-full bg-[#0a0d14] border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-[#d4af37] min-h-[100px] resize-none"
                      data-testid="lead-notes-textarea"
                    />
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => { setEditingNotes(false); setNotesText(selectedLead.notes || ''); }}
                        className="px-3 py-1.5 text-gray-400 text-sm hover:text-white"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={saveNotes}
                        disabled={savingNotes}
                        className="px-4 py-1.5 bg-[#d4af37] text-[#0a0d14] rounded-lg text-sm font-medium disabled:opacity-50"
                        data-testid="save-notes-btn"
                      >
                        {savingNotes ? 'Saving...' : 'Save Notes'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-[#0a0d14]/50 rounded-lg p-3 sm:p-4 min-h-[60px]">
                    {selectedLead.notes ? (
                      <p className="text-gray-300 text-sm whitespace-pre-wrap">{selectedLead.notes}</p>
                    ) : (
                      <p className="text-gray-500 text-sm italic">No notes yet</p>
                    )}
                  </div>
                )}
              </div>

              {/* Status Update */}
              <div>
                <h4 className="text-[#d4af37] text-xs font-medium mb-3 uppercase tracking-wider">Update Status</h4>
                <div className="flex flex-wrap gap-2">
                  {['NEW', 'CONTACTED', 'QUALIFIED', 'CLOSED'].map(status => (
                    <button
                      key={status}
                      onClick={() => updateLeadStatus(selectedLead.id, status)}
                      className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
                        selectedLead.status === status
                          ? 'bg-[#d4af37] text-[#0a0d14]'
                          : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      }`}
                      data-testid={`status-btn-${status.toLowerCase()}`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tracking Details */}
              <div>
                <h4 className="text-[#d4af37] text-xs font-medium mb-3 uppercase tracking-wider">Tracking Details</h4>
                <div className="bg-[#0a0d14]/50 rounded-lg p-3 sm:p-4 space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Date</span>
                    <span className="text-gray-300">{formatDate(selectedLead.created_at)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Source</span>
                    <span className="text-gray-300">{selectedLead.utm_source || selectedLead.referrer || 'Direct'}</span>
                  </div>
                  {selectedLead.utm_medium && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Medium</span>
                      <span className="text-gray-300">{selectedLead.utm_medium}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-500">Lead ID</span>
                    <span className="text-gray-500 font-mono text-[10px] sm:text-xs truncate ml-4">{selectedLead.id}</span>
                  </div>
                </div>
              </div>

              {/* Email Status */}
              <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm">
                <div className={`flex items-center gap-2 ${selectedLead.owner_email_sent ? 'text-green-400' : 'text-gray-500'}`}>
                  {selectedLead.owner_email_sent ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  Owner Notified
                </div>
                <div className={`flex items-center gap-2 ${selectedLead.autoresponse_sent ? 'text-green-400' : 'text-gray-500'}`}>
                  {selectedLead.autoresponse_sent ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  Autoresponse Sent
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
